import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * 認證處理Hook
 * 用於處理使用者登入、登出和會話管理
 * 
 * @returns {Object} 認證狀態和操作方法
 */
function useAuth() {
    // 從本地存儲中獲取初始狀態
    const [token, setToken] = useState(localStorage.getItem('token') || null);
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(!!token);
    const [sessionExpiry, setSessionExpiry] = useState(localStorage.getItem('sessionExpiry') || null);
    const [csrfToken, setCsrfToken] = useState(localStorage.getItem('csrfToken') || null);

    // 設置 axios 默認 headers
    useEffect(() => {
        if (csrfToken) {
            axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
        } else {
            delete axios.defaults.headers.common['X-CSRF-Token'];
        }
    }, [csrfToken]);

    // 登入
    const login = useCallback(async (username, password) => {
        try {
            setLoading(true);
            setError(null);
            
            // 發送登錄請求
            const response = await axios.post('/api/auth/login', { username, password });
            
            const { token: newToken, user: userData, csrfToken: newCsrfToken, expiresIn } = response.data;

            // 計算過期時間
            const expiryTime = new Date();
            expiryTime.setSeconds(expiryTime.getSeconds() + (expiresIn || 86400)); // 默認 24 小時

            // 保存到本地存儲
            localStorage.setItem('token', newToken);
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('sessionExpiry', expiryTime.toISOString());
            localStorage.setItem('csrfToken', newCsrfToken);

            // 更新狀態
            setToken(newToken);
            setUser(userData);
            setIsAuthenticated(true);
            setSessionExpiry(expiryTime.toISOString());
            setCsrfToken(newCsrfToken);

            // 設置 axios 默認 headers
            axios.defaults.headers.common['X-CSRF-Token'] = newCsrfToken;

            return true;
        } catch (err) {
            console.error('登入失敗:', err);
            setError(err.response?.data?.error || '登入失敗，請檢查您的憑據');
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    // 登出
    const logout = useCallback(async () => {
        try {
            // 確保使用當前的 CSRF 令牌
            if (csrfToken) {
                axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
            }

            // 呼叫登出 API
            await axios.post('/api/auth/logout');
        } catch (error) {
            console.error('登出 API 呼叫失敗:', error);
        } finally {
            // 無論 API 呼叫成功與否，都清除本地存儲
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('sessionExpiry');
            localStorage.removeItem('csrfToken');

            // 更新狀態
            setToken(null);
            setUser(null);
            setIsAuthenticated(false);
            setSessionExpiry(null);
            setCsrfToken(null);

            // 清除 axios 默認 headers
            delete axios.defaults.headers.common['X-CSRF-Token'];
        }
    }, [csrfToken]);

    // 檢查會話是否過期
    const checkSessionExpiry = useCallback(() => {
        if (!sessionExpiry) return true;

        const now = new Date();
        const expiry = new Date(sessionExpiry);

        return now >= expiry;
    }, [sessionExpiry]);

    // 獲取剩餘會話時間（秒）
    const getSessionTimeRemaining = useCallback(() => {
        if (!sessionExpiry) return 0;

        const now = new Date();
        const expiry = new Date(sessionExpiry);
        const diffMs = expiry - now;

        return Math.max(0, Math.floor(diffMs / 1000));
    }, [sessionExpiry]);

    // 監聽會話過期
    useEffect(() => {
        if (isAuthenticated && sessionExpiry) {
            const checkInterval = setInterval(() => {
                if (checkSessionExpiry()) {
                    logout();
                    clearInterval(checkInterval);
                }
            }, 10000); // 每10秒檢查一次

            return () => clearInterval(checkInterval);
        }
    }, [isAuthenticated, sessionExpiry, checkSessionExpiry, logout]);

    return {
        token,
        user,
        loading,
        error,
        isAuthenticated,
        sessionExpiry,
        csrfToken,
        login,
        logout,
        checkSessionExpiry,
        getSessionTimeRemaining
    };
}

export default useAuth; 